import React, { memo, FC } from "react";
import Typography from "@mui/material/Typography";
import Choices from "../../Choices";
import Grow from "@mui/material/Grow";
import SwingWidthAccordian from "../Width/SwingWidthAccordian";
import SwingConfigs from "../Config/SwingConfigs";
import EqualPanels from "../EqualPanels/EqualPanels";
import ReusableAccordian from "../../ReusableAccordion";
import PleaseNote from "../../PleaseNote";
import { pairSideLite, sideLite, swingDirection, swingType } from "./defaults";
import { RowLayout, ColLayout } from "../../../util/accordianStyles";
import { styled } from "@mui/material/styles";
import useSwingDirectionSelector from "./useSwingDirectionSelector";

const RowLayoutTopDivider = styled("div")(({ theme }) => ({
  marginBottom: 10,
  marginTop: 20,
  alignItems: "center",
  display: "flex",
  borderTop: `2px solid ${theme.palette.divider}`,
  padding: theme.spacing(1, 2),
  width: "100%",
  justifyContent: "space-between",
}));

interface SwingDirectionProps {
  editQuote: boolean;
  submitPressed: boolean;
}

const SwingDirectionAccordian: FC<SwingDirectionProps> = ({
  editQuote,
  submitPressed,
}) => {
  const {
    widthMeasureImg,
    setWidthMeasureImg,
    handleClickSLite,
    handleClickSType,
    handleClickSDir,
    editBtn,
    direction,
    sType,
    sLite,
    isAlum
  } = useSwingDirectionSelector(submitPressed, editQuote);

  return (
    <>
      <ReusableAccordian
        accordionName="swingDirection"
        heading="Configuration"
        secondaryHeading="Select Configuration"
        colOrRow="col"
        required5={true}
      >
        {direction === "Inswing" && (
          <PleaseNote notes="No warranty for water penetration, wind penetration, or airborne particles with this option" />
        )}
        <RowLayout style={{ justifyContent: "flex-start", width: "100%" }}>
          <ColLayout style={{ width: "60%" }}>
            <RowLayout
              style={{ width: "100%", justifyContent: "space-between" }}
            >
              <Typography style={{ flex: 1 }}>Swing Direction:</Typography>
              <RowLayout style={{ marginRight: 100 }}>
                {swingDirection.map((state) => (
                  <div
                    key={state.id}
                    onClick={() => handleClickSDir(state.label)}
                  >
                    <Choices
                      label={state.label}
                      variant={
                        state.label === direction ? "filled" : "outlined"
                      }
                      checkMark={state.label === direction}
                    />
                  </div>
                ))}
              </RowLayout>
            </RowLayout>

            {direction && (
              <Grow in={true} timeout={500}>
                <RowLayoutTopDivider>
                  <Typography style={{ flex: 1 }}>Swing Type:</Typography>
                  <RowLayout style={{ marginRight: 60 }}>
                    {swingType.map((state) => (
                      <div
                        key={state.id}
                        onClick={() => handleClickSType(state.label)}
                      >
                        <Choices
                          label={state.label}
                          variant={
                            state.label === sType ? "filled" : "outlined"
                          }
                          checkMark={state.label === sType}
                        />
                      </div>
                    ))}
                  </RowLayout>
                </RowLayoutTopDivider>
              </Grow>
            )}

            {direction && sType ? (
              <Grow in={true} timeout={500}>
                <RowLayoutTopDivider>
                  <Typography style={{ flex: 1 }}>Sidelite:</Typography>

                  <RowLayout
                    style={{ paddingRight: sType === "Pair Swing" && !isAlum ? 100 : 0 }}
                  >
                    {sType === "Single Swing" || isAlum
                      ? sideLite.map((state) => (
                          <div
                            key={state.id}
                            onClick={() => handleClickSLite(state.label)}
                          >
                            <Choices
                              label={state.label}
                              variant={
                                state.label === sLite ? "filled" : "outlined"
                              }
                              checkMark={state.label === sLite}
                            />
                          </div>
                        ))
                      : pairSideLite.map((state) => (
                          <div
                            key={state.id}
                            onClick={() => handleClickSLite(state.label)}
                          >
                            <Choices
                              label={state.label}
                              variant={
                                state.label === sLite ? "filled" : "outlined"
                              }
                              checkMark={state.label === sLite}
                            />
                          </div>
                        ))}
                  </RowLayout>
                </RowLayoutTopDivider>
              </Grow>
            ) : null}

            {direction && sType && sLite !== "none" ? (
              <Grow in={true} timeout={500}>
                <RowLayout style={{ width: "100%" }}>
                  <EqualPanels />
                </RowLayout>
              </Grow>
            ) : null}
          </ColLayout>

          {(sType || direction) && (
            <ColLayout>
              <RowLayoutTopDivider style={{ borderTop: "none" }}>
                <SwingConfigs
                  setWidthMeasureImg={setWidthMeasureImg}
                  sType={sType || ""}
                  sLite={sLite || ""}
                  swingDir={direction || ""}
                />
              </RowLayoutTopDivider>
            </ColLayout>
          )}
        </RowLayout>
      </ReusableAccordian>

      <SwingWidthAccordian
        widthMeasureImg={widthMeasureImg}
        editBtn={editBtn}
        sideLites={sLite || ""}
        swingType={sType || ""}
        direction={direction || ""}
      />
    </>
  );
};

export default memo(SwingDirectionAccordian);
